import { NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DevExtremeModule } from 'devextreme-angular';
import { IboxtoolsModule } from '../common/iboxtools/iboxtools.module';
import { baseZIndex } from 'devextreme/ui/overlay';
import { Options as dxDataGridOptions, default as dxDataGrid } from 'devextreme/ui/data_grid';
import { Options as dxTreeListOptions, default as dxTreeList } from 'devextreme/ui/tree_list';
import { Options as dxFormOptions, default as dxForm } from 'devextreme/ui/form';
import { Options as dxSelectBoxOptions, default as dxSelectBox } from 'devextreme/ui/select_box';
import { ModalModule } from 'ngx-bootstrap/modal';
import { HrefDirective } from './href.directive';
import { RouterModule } from '@angular/router';
import { DateDialogComponent } from './date-dialog/date-dialog.component';
import { InputDialogComponent } from './input-dialog/input-dialog.component';
import { SelectDialogComponent } from './select-dialog/select-dialog.component';
import { ListDialogComponent } from './list-dialog/list-dialog.component';
import { DetailDialogComponent } from './detail-dialog/detail-dialog.component';
import { EmailDialogComponent } from './email-dialog/email-dialog.component';
import { AuditDialogComponent } from './audit-dialog/audit-dialog.component';
import { ReportPreviewDialogComponent } from './report-preview-dialog/report-preview-dialog.component';
// import { ReportDialogComponent } from './report-dialog/report-dialog.component';
import { BannerDialogComponent } from './banner-dialog/banner-dialog.component';
import { exportDataGrid } from 'devextreme/excel_exporter';
import { Workbook } from 'exceljs';
import saveAs from 'file-saver';
import _ from 'lodash';

export { DateDialogComponent, InputDialogComponent };

@NgModule({
    imports: [CommonModule, FormsModule, ModalModule, RouterModule, DevExtremeModule, IboxtoolsModule],
    declarations: [
        HrefDirective,
        DateDialogComponent,
        InputDialogComponent,
        SelectDialogComponent,
        ListDialogComponent,
        DetailDialogComponent,
        EmailDialogComponent,
        AuditDialogComponent,
        // ReportDialogComponent,
        BannerDialogComponent,
        ReportPreviewDialogComponent,
    ],
    exports: [
        DevExtremeModule,
        IboxtoolsModule,
        HrefDirective,
        DateDialogComponent,
        InputDialogComponent,
        SelectDialogComponent,
        ListDialogComponent,
        DetailDialogComponent,
        AuditDialogComponent,
    ],
})
export class SharedModule {
    constructor() {
        const dataGridOptions: dxDataGridOptions = {
            height: '100px' /* hack default value to make scrolling work */,
            width: '100%',
            columnAutoWidth: true,
            columnResizingMode: 'widget',
            allowColumnReordering: true,
            allowColumnResizing: true,
            hoverStateEnabled: false,
            showBorders: true,
            showColumnLines: false,
            showRowLines: true,
            editing: { allowUpdating: false },
            paging: { enabled: false, pageSize: 50 },
            scrolling: { mode: 'virtual', showScrollbar: 'always', preloadEnabled: true },
            selection: { mode: 'single', showCheckBoxesMode: 'onLongTap' },
            stateStoring: { savingTimeout: 800 },
            filterRow: { applyFilter: 'auto' },
            columnChooser: { enabled: true, search: { enabled: true }, mode: 'select', height: 500 },
            focusedRowEnabled: false,
            customizeColumns: (columns: any) => {
                columns.forEach(column => {
                    column.allowEditing = false;
                }); 
            },
            onExporting: SharedModule.onExporting,
            onToolbarPreparing: SharedModule.onToolbarPreparing,
            onOptionChanged: SharedModule.onOptionChanged,
        };
        dxDataGrid.defaultOptions({ options: dataGridOptions });

        const treeListOptions: dxTreeListOptions = {
            // height: '100px',
            width: '100%',
            allowColumnResizing: true,
            columnResizingMode: 'widget',
            showBorders: true,
            hoverStateEnabled: false,
            selection: { mode: 'single' },
            stateStoring: { savingTimeout: 800 },
        };
        dxTreeList.defaultOptions({ options: treeListOptions });

        const formOptions: dxFormOptions = {
            showColonAfterLabel: false,
            labelLocation: 'left',
            // labelLocation: 'top',
        };
        dxForm.defaultOptions({ options: formOptions });

        const selectboxOptions: dxSelectBoxOptions = {
            searchEnabled: true,
            searchTimeout: 100,
            showClearButton: false,
            inputAttr: { autoComplete: false },
            onFocusIn: (e: any) => {
                const inputs = e.element.getElementsByTagName('input');
                inputs.item(inputs.length - 1)?.select();
            },
        };
        dxSelectBox.defaultOptions({ options: selectboxOptions });

        baseZIndex(3000);
    }

    public static async onToolbarPreparing(e: any) {
        e.toolbarOptions.items.unshift(
            {
                location: 'before',
                template: 'toolbarButtons',
            },
            {
                template: 'toolbarText',
                location: 'after',
            },
            {
                location: 'after',
                widget: 'dxButton',
                options: {
                    icon: 'revert',
                    elementAttr: { title: 'Reset Filters' },
                    onClick: () => {
                        e.component.clearFilter();
                    },
                },
            },
            {
                location: 'after',
                widget: 'dxButton',
                options: {
                    icon: 'refresh',
                    elementAttr: { title: 'Reset Grid Layout' },
                    onClick: () => {
                        e.component.state({});
                    },
                },
            }
        );
    }

    public static onOptionChanged(e) {
        if (e.fullName === 'searchPanel.text') {
            e.component.clearSelection();
        }
        const filterCheck = _.includes(e.fullName, 'filterValue');
        if (filterCheck) {
            e.component.clearSelection();
        }
    }

    public static async onExporting(e: any, fileName: string = 'export', worksheetName: string = 'Data') {
        const workbook = new Workbook();
        const worksheet = workbook.addWorksheet(worksheetName);
        await exportDataGrid({ component: e.component, worksheet: worksheet });
        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer], { type: 'application/octet-stream' }), `${fileName}.xlsx`);
    }
}
